/**
 * Schema Validator — 올린 JSON 을 읽어 v1 · v2 를 가르고, 틀린 곳을 경로로 알려 준다.
 *   예: 「geometry.parts[2].size 가 없습니다」 · 「motion.animations[0].target 「tray」 part 가 없습니다」
 * 새 라이브러리 없이 직접 검사한다. 통과하면 저장용 정의(StoredDefinition)와 파라미터 값을 돌려준다.
 *
 * 결과 종류
 *   v2-json   = geometry 로 그린다(JSON 정의 장비)
 *   v2-builtin / v1 = 트윈 종류(spec_id) + 파라미터 — 형상은 기존 트윈 코드(이 JSON 에 형상 정의 없음)
 *
 * ★ 정본(2026-10-12 P1) — 서버도 이 파일을 그대로 쓴다: scripts/gen-twin-validator.js 가 이 파일과 의존 파일
 *   (expr · resolve · types · safeJson)을 CommonJS 로 옮겨 lib/generated/twin-definition/ 에 둔다.
 *   여기를 고치면 `node scripts/gen-twin-validator.js` 를 다시 돌린다 — 안 돌리면 twinValidatorGenerated.test.ts 가 실패한다.
 *   three 같은 무거운 의존을 끌어오지 않는다(서버 번들에 들어간다).
 *
 * 검증 수준
 *   L1 안전   — 크기(2MB) · JSON 문법 · 금지 키(__proto__ · prototype · constructor) · NaN/Infinity · 깊이(safeJson)
 *   L2 형식   — format 은 없거나(옛 파일 · DB 저장본) 정확히 twin3d/v1 · twin3d/v2. 그 밖의 값은 오류
 *   L3 모양   — spec_id · spec_name · param_schema · materials · geometry.parts(type · size · id) · motion · bindings 칸의 꼴
 *   L4 참조   — spec_id 가 트윈 종류(hasBuiltin)인지 · part.material → materials · motion target → part · path → motion.paths
 *              · sequence start → sequences · binding target · part/animation/path/sequence id 중복
 *   L5 계산   — 식(expr 화이트리스트 — eval 없음) · 실제로 풀어 본 크기 > 0 · 위치 · 회전 finite · mesh 번호 범위 · 개수 상한
 */
import { compileExpr } from "./expr";
import { MAX_REPEAT, itemSize, parseTarget, resolveDefinition } from "./resolve";
import { KIT_NAMES } from "./kitNames";
import { JSON_SPEC_ID, PRIMITIVES, TWIN3D_V1, TWIN3D_V2, TWIN_KINDS } from "./types";
import { scanUnsafeJson } from "./safeJson";
import type { Issue, ParamSchemaEntry, StoredDefinition } from "./types";

export type Validation =
  | { ok: false; errors: Issue[]; warnings: Issue[] }
  | {
    ok: true;
    kind: "v1" | "v2-builtin" | "v2-json";
    format: string;
    specId: string;
    specName: string | null;
    model: { id: string | null; name: string | null; model_no: string | null; manufacturer: string | null };
    params: Record<string, unknown>;
    /** v2-json 일 때만 */
    definition: StoredDefinition | null;
    counts: { parts: number; animations: number; sequences: number; params: number; bindings: number; materials: number; derived: number };
    warnings: Issue[];
    /** 사람에게 보일 한 줄 안내(예: v1 — 형상 정의 없음) */
    notes: string[];
  };

const ID = /^[A-Za-z0-9_-]{1,60}$/;
const HEX = /^#[0-9a-fA-F]{6}$/;
export const MAX_PARTS = 1000;
export const MAX_TEXT = 2_000_000;
export const MAX_MESH_NUMBERS = 600_000;
/** mesh 삼각형 번호 숫자 수 상한(꼭짓점 숫자 상한의 2배 — 꼭짓점 하나를 삼각형 여럿이 나눠 쓰므로) */
export const MAX_MESH_INDICES = 1_200_000;

/** 트윈 코드판 종류(30) — 서버 · 화면이 같은 목록으로 spec_id 를 확인한다(registry 와 같은지 테스트가 지킨다) */
export const isBuiltinSpecId = (id: unknown): boolean => typeof id === "string" && TWIN_KINDS.includes(id);

const isObj = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);

/** [STEP 2-A] GLB 자산 주소 — 같은 출처 /twin-assets/ 아래 .glb 경로만(디렉터리 · 파일 이름은 영문 · 숫자 · _ · -, 파일 이름 안 점은 한 칸씩) */
export const TWIN_ASSET_PREFIX = "/twin-assets/";
const ASSET_PATH = /^\/twin-assets\/(?:[A-Za-z0-9_-]+\/)*[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)*\.glb$/;
const MAX_ASSET_URL = 200;
/**
 * 자산 주소 검사 — 문제가 있으면 이유, 없으면 null. 캐시 열쇠도 이 검사를 통과한 경로 그대로 쓴다(assetCache).
 *   막는 것: http(s):// · // · data: · blob: · 역슬래시 · .. · % 인코딩 · ? · # · 공백 · /twin-assets/ 밖 · .glb 아님.
 *   URL 로 풀어 본 pathname 이 원래 글자와 다르면(정규화로 바뀌는 꼴) 막는다.
 */
export function assetUrlIssue(url: unknown): string | null {
  if (typeof url !== "string" || !url) return "주소가 없습니다";
  if (url.length > MAX_ASSET_URL) return `주소가 너무 깁니다(${MAX_ASSET_URL}자까지)`;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith("//")) return "외부 주소 · data: · blob: 는 쓸 수 없습니다 — 같은 서버의 /twin-assets/… .glb 경로만";
  if (/[\\%?#\s]/.test(url) || url.includes("..") || url.includes("//")) return "경로에 \\ · % · ? · # · 공백 · .. · // 를 쓸 수 없습니다";
  if (!url.startsWith(TWIN_ASSET_PREFIX)) return `${TWIN_ASSET_PREFIX} 아래 경로만 씁니다`;
  if (!ASSET_PATH.test(url)) return "경로는 /twin-assets/<이름>.glb 꼴(영문 · 숫자 · _ · -)이어야 합니다";
  try { if (new URL(url, "http://same-origin.invalid").pathname !== url) return "정규화하면 바뀌는 경로입니다"; } catch { return "주소를 읽지 못했습니다"; }
  return null;
}

/**
 * 형상 정의(JSON 3D)가 있는가 — geometry.parts 배열 또는 type "asset"(GLB · parts 는 없어도 됨 · 있으면 혼합).
 *   검사기와 카탈로그 가져오기 감지(catalogTransfer · importText)가 같은 규칙을 쓴다 — 「parts: [] 를 넣어야 인식」 같은 숨은 요구가 없게.
 *   모양이 틀린 것(asset 칸 없음 등)은 여기서 거르지 않고 검사기가 경로로 알려 준다.
 */
export function hasJsonGeometry(g: unknown): boolean {
  return isObj(g) && (Array.isArray(g.parts) || g.type === "asset");
}

/** [STEP 2-B] 카탈로그 3D 자산 id(UUID, 대소문자 무관) */
export const ASSET_ID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const finite3 = (v: unknown): v is [number, number, number] => Array.isArray(v) && v.length === 3 && v.every((n) => typeof n === "number" && Number.isFinite(n));

/** [STEP 2-A] geometry.type = "asset" — GLB · m · Y 위 · footprint 필수 · transform 은 숫자 3개(식 아님) · parts 는 있어도 되고(혼합) 없어도 됨 */
function checkAsset(g: Record<string, unknown>, err: (path: string, message: string) => void) {
  if (g.unit !== undefined && g.unit !== "m") err("geometry.unit", "GLB 자산은 m 만 씁니다(glTF 표준) — unit 을 빼거나 \"m\"");
  if (g.footprint === undefined) err("geometry.footprint", "GLB 자산은 바닥 크기 [가로, 세로](m)가 필요합니다 — 불러오기 전에도 배치 · 충돌을 계산합니다");
  else if (!Array.isArray(g.footprint) || g.footprint.length !== 2 || !g.footprint.every((n) => typeof n === "number" && Number.isFinite(n) && n > 0)) err("geometry.footprint", "GLB 자산의 footprint 는 0 보다 큰 숫자 두 개 [가로, 세로](m)");
  const a = g.asset;
  if (!isObj(a)) { err("geometry.asset", "{ format: \"glb\", url: \"/twin-assets/….glb\" } 가 필요합니다"); return; }
  if (a.format === "gltf") err("geometry.asset.format", "glTF(+bin)는 아직 받지 않습니다 — 한 파일 GLB(\"glb\")로 내보내세요");
  else if (a.format !== "glb") err("geometry.asset.format", "\"glb\" 만 씁니다");
  // [STEP 2-B] url(정적 개발용) XOR asset_id(운영 — 카탈로그 자산 표 UUID). 둘 다 · 둘 다 없음 = 오류
  const hasUrl = a.url !== undefined; const hasId = a.asset_id !== undefined;
  if (hasUrl && hasId) err("geometry.asset", "url 과 asset_id 는 둘 중 하나만 씁니다");
  else if (!hasUrl && !hasId) err("geometry.asset", "url(/twin-assets/….glb) 또는 asset_id(카탈로그 3D 자산) 가 필요합니다");
  else if (hasId) { if (typeof a.asset_id !== "string" || !ASSET_ID.test(a.asset_id)) err("geometry.asset.asset_id", "UUID 여야 합니다"); }
  else { const issue = assetUrlIssue(a.url); if (issue) err("geometry.asset.url", issue); }
  if (a.position !== undefined && !finite3(a.position)) err("geometry.asset.position", "숫자 세 개 [x, y, z](m)");
  if (a.rotation !== undefined && !finite3(a.rotation)) err("geometry.asset.rotation", "숫자 세 개 [x, y, z](도)");
  if (a.scale !== undefined) {
    if (!finite3(a.scale)) err("geometry.asset.scale", "숫자 세 개 [x, y, z] — 한 숫자(scalar)는 쓰지 않습니다");
    else if (a.scale.some((n) => n <= 0)) err("geometry.asset.scale", "0 보다 커야 합니다");
  }
  if (a.lod !== undefined && a.lod !== "hi") err("geometry.asset.lod", "\"hi\"(가까이 볼 때만 GLB) 만 씁니다");
  for (const k of Object.keys(a)) if (!["format", "url", "asset_id", "position", "rotation", "scale", "lod"].includes(k)) err(`geometry.asset.${k}`, "모르는 칸입니다(format · url | asset_id · position · rotation · scale · lod)");
}

/** 텍스트 → 검사. hasBuiltin = 트윈에 그 종류가 있는가(v1 · v2-builtin 확인용) */
export function validateTwinText(text: string, hasBuiltin: (id: string) => boolean): Validation {
  if (text.length > MAX_TEXT) return { ok: false, errors: [{ path: "(파일)", message: `너무 큽니다(${Math.round(MAX_TEXT / 1000)}KB 까지)` }], warnings: [] };
  let raw: unknown;
  try { raw = JSON.parse(text); } catch (e) { return { ok: false, errors: [{ path: "(파일)", message: `JSON 형식이 아닙니다 — ${(e as Error).message}` }], warnings: [] }; }
  return validateTwinJson(raw, hasBuiltin);
}

export function validateTwinJson(raw: unknown, hasBuiltin: (id: string) => boolean): Validation {
  const errors: Issue[] = [];
  const warnings: Issue[] = [];
  const err = (path: string, message: string) => errors.push({ path, message });
  const warn = (path: string, message: string) => warnings.push({ path, message });
  if (!isObj(raw)) return { ok: false, errors: [{ path: "(맨 바깥)", message: "{ … } 객체여야 합니다" }], warnings };
  const o = raw;
  if (Array.isArray(o.assets)) return { ok: false, errors: [{ path: "assets", message: "설비 일괄 파일입니다 — 화면 위 「설비 추가 › 가져오기」에 올리세요" }], warnings };
  // L1 — 금지 키 · NaN/Infinity · 깊이(먼저 보고 멈춘다 — 뒤 검사가 오염된 객체를 만지지 않게)
  const unsafe = scanUnsafeJson(o);
  if (unsafe.length) return { ok: false, errors: unsafe, warnings };

  // L2 — format 은 없거나(옛 파일 · DB 저장본 · 일괄 파일 twin) 정확히 v1 · v2. 모르는 값은 오류(예전: 경고 후 v2 로 읽음)
  if (o.format !== undefined && o.format !== TWIN3D_V1 && o.format !== TWIN3D_V2) {
    return { ok: false, errors: [{ path: "format", message: `「${String(o.format)}」 — 모르는 형식입니다. ${TWIN3D_V1} 또는 ${TWIN3D_V2} 만 받습니다` }], warnings };
  }
  const format = str(o.format) || "";
  const m = isObj(o.model) ? o.model : {};
  const model = { id: str(m.id), name: str(m.name), model_no: str(m.model_no), manufacturer: str(m.manufacturer) };
  const params = isObj(o.params) ? o.params : {};
  if (o.params !== undefined && !isObj(o.params)) err("params", "{ 이름: 값 } 객체여야 합니다");
  const specIdRaw = str(o.spec_id);
  const hasGeometry = hasJsonGeometry(o.geometry);

  // ── 형상 정의 없음 = v1 · v2-builtin (기존 트윈 종류) ─────────────────
  if (!hasGeometry) {
    if (!specIdRaw || specIdRaw === JSON_SPEC_ID) {
      err("geometry", "geometry.parts 가 없습니다 — 형상을 정의하거나, spec_id 에 기존 트윈 종류를 적으세요");
      return { ok: false, errors, warnings };
    }
    if (!hasBuiltin(specIdRaw)) { err("spec_id", `트윈에 「${specIdRaw}」 종류가 없습니다`); return { ok: false, errors, warnings }; }
    if (errors.length) return { ok: false, errors, warnings };
    return {
      ok: true, kind: format === TWIN3D_V1 || !format ? "v1" : "v2-builtin", format: format || TWIN3D_V1,
      specId: specIdRaw, specName: str(o.spec_name), model, params, definition: null,
      counts: { parts: 0, animations: 0, sequences: 0, params: Object.keys(params).length, bindings: 0, materials: 0, derived: 0 },
      warnings,
      notes: ["이 JSON 에는 3D 형상 정의(geometry)가 없습니다. 기존 트윈 종류의 파라미터 모델로만 씁니다."],
    };
  }

  // ── v2 JSON 정의 ───────────────────────────────────────────────────
  if (specIdRaw && specIdRaw !== JSON_SPEC_ID) warn("spec_id", `geometry 가 있어 「${specIdRaw}」 대신 이 JSON 으로 그립니다`);
  const specName = str(o.spec_name);
  if (!specName) err("spec_name", "이름이 없습니다");

  // param_schema
  const schema: ParamSchemaEntry[] = [];
  const keys = new Set<string>();
  if (o.param_schema !== undefined && !Array.isArray(o.param_schema)) err("param_schema", "배열이어야 합니다");
  (Array.isArray(o.param_schema) ? o.param_schema : []).forEach((e: unknown, i: number) => {
    const p = `param_schema[${i}]`;
    if (!isObj(e)) { err(p, "객체여야 합니다"); return; }
    const key = str(e.key);
    if (!key || !/^[A-Za-z_][A-Za-z0-9_]{0,40}$/.test(key)) { err(`${p}.key`, "영문 · 숫자 · _ 이름이 필요합니다"); return; }
    if (keys.has(key)) err(`${p}.key`, `「${key}」 가 두 번 나옵니다`);
    keys.add(key);
    const type = e.type;
    if (type !== "int" && type !== "float" && type !== "select" && type !== "bool") { err(`${p}.type`, "int · float · select · bool 중 하나"); return; }
    const entry: ParamSchemaEntry = { key, type, label: str(e.label) || key, default: e.default as number | string | boolean };
    if (str(e.unit)) entry.unit = str(e.unit)!;
    if (type === "int" || type === "float") {
      if (typeof e.default !== "number") err(`${p}.default`, "숫자여야 합니다");
      for (const k of ["min", "max", "step"] as const) if (e[k] !== undefined) { if (typeof e[k] !== "number") err(`${p}.${k}`, "숫자여야 합니다"); else entry[k] = e[k] as number; }
      if (typeof entry.min === "number" && typeof entry.max === "number" && entry.min > entry.max) err(p, "min 이 max 보다 큽니다");
    } else if (type === "select") {
      const opts = Array.isArray(e.options) ? e.options : [];
      if (!opts.length) err(`${p}.options`, "선택지가 없습니다");
      entry.options = opts.map((x: unknown) => (isObj(x) ? { value: String(x.value), label: str(x.label) || String(x.value) } : { value: String(x), label: String(x) }));
      if (!entry.options.some((x) => x.value === String(e.default))) err(`${p}.default`, "선택지 중 하나여야 합니다");
    } else if (typeof e.default !== "boolean") err(`${p}.default`, "true · false 여야 합니다");
    // [N9] 옛 키(aliases — 저장본 옛 이름) · 보이기 조건(show_if — 다른 칸 값)
    if (e.aliases !== undefined) {
      if (!Array.isArray(e.aliases) || e.aliases.some((a: unknown) => typeof a !== "string" || !/^[A-Za-z_][A-Za-z0-9_]{0,40}$/.test(a))) err(`${p}.aliases`, "영문 · 숫자 · _ 이름 목록이어야 합니다");
      else if (e.aliases.length) entry.aliases = (e.aliases as string[]).slice();
    }
    if (e.show_if !== undefined) {
      const si = e.show_if;
      if (!isObj(si) || !str(si.key) || !Array.isArray(si.values) || !si.values.length) err(`${p}.show_if`, "{ key: 칸 이름, values: [값 …] } 이어야 합니다");
      else entry.show_if = { key: str(si.key)!, values: (si.values as unknown[]).map(String) };
    }
    schema.push(entry);
  });
  schema.forEach((e, i) => {
    if (e.show_if && !keys.has(e.show_if.key)) err(`param_schema[${i}].show_if.key`, `「${e.show_if.key}」 가 param_schema 에 없습니다`);
    (e.aliases || []).forEach((a) => { if (keys.has(a)) err(`param_schema[${i}].aliases`, `「${a}」 는 다른 칸 이름입니다 — 옛 키는 지금 칸과 겹치면 안 됩니다`); });
  });
  Object.keys(params).filter((k) => !keys.has(k)).forEach((k) => warn(`params.${k}`, "param_schema 에 없는 값 — 무시합니다"));

  // $vars — derived 이름(순서대로) + 동작 순서의 변수(pick · set)
  const hasOwn = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);
  const knownVars = new Set<string>();
  const motionPre = isObj(o.motion) ? o.motion : {};
  // 동작 순서의 변수(pick · set · repeat 의 var) — parallel · if · repeat 안까지
  const scanVars = (steps: unknown, depth: number) => {
    if (!Array.isArray(steps) || depth > 8) return;
    steps.forEach((st: unknown) => {
      if (!isObj(st)) return;
      if ((st.op === "pick" || st.op === "set" || st.op === "repeat" || st.op === "during") && typeof st.var === "string") knownVars.add(st.var.replace(/\[.*$/, "")); // c[식] → c
      if (st.op === "start" && isObj(st.with)) Object.keys(st.with).forEach((k2) => knownVars.add(k2));
      for (const k of ["steps", "then", "else"]) scanVars(st[k], depth + 1);
    });
  };
  (Array.isArray(motionPre.sequences) ? motionPre.sequences : []).forEach((sq: unknown) => {
    if (!isObj(sq)) return;
    scanVars(sq.steps, 0);
    if (isObj(sq.for) && typeof sq.for.var === "string") knownVars.add(sq.for.var);
    if (Array.isArray(sq.local)) sq.local.forEach((n: unknown) => { if (typeof n === "string") knownVars.add(n.replace(/\[.*$/, "")); });
  });
  /** allowFlow = $flow(흐름 연동 상태)를 쓸 수 있는 칸 — 동작 · part.visible 만(형상은 놓을 때 한 번 정해진다) */
  const checkExpr = (path: string, v: unknown, allowValue = false, extraVars: string[] = [], allowFlow = false) => {
    if (typeof v === "number") { if (!Number.isFinite(v)) err(path, "숫자가 아닙니다"); return; }
    if (typeof v !== "string") { err(path, "숫자 또는 식이어야 합니다"); return; }
    const c = compileExpr(v);
    if (!c.ok) { err(path, `식 오류 — ${c.error}`); return; }
    if (c.expr.usesValue && !allowValue) err(path, "$value 는 bindings 의 expression 에서만 씁니다");
    c.expr.params.filter((k) => !keys.has(k)).forEach((k) => err(path, `$params.${k} — param_schema 에 없습니다`));
    // 번호 붙은 변수 $vars.x[k] — x 또는 derived x_0 · x_1 … 이 있으면 안다
    const knownBase = (k: string) => knownVars.has(k) || extraVars.includes(k) || Array.from(knownVars).some((n) => n.startsWith(`${k}_`) && /^\d+$/.test(n.slice(k.length + 1)));
    c.expr.vars.filter((k) => !knownBase(k)).forEach((k) => err(path, `$vars.${k} — derived(앞쪽) 또는 동작 순서(pick · set)에 없습니다`));
    if (c.expr.usesFlow && !allowFlow) err(path, "$flow 는 동작(animations · sequences)과 part.visible 에서만 씁니다");
  };
  const checkFlow = (path: string, v: unknown) => checkExpr(path, v, false, [], true);
  const checkVec = (path: string, v: unknown, required: boolean, extraVars: string[] = [], allowFlow = false) => {
    if (v === undefined) { if (required) err(path, "가 없습니다"); return; }
    if (!Array.isArray(v) || v.length !== 3) { err(path, "[x, y, z] 세 값이어야 합니다"); return; }
    v.forEach((x, i) => checkExpr(`${path}[${i}]`, x, false, extraVars, allowFlow));
  };

  // derived — 순서대로(뒤의 것은 앞의 것만 쓴다)
  if (o.derived !== undefined && !Array.isArray(o.derived)) err("derived", "배열이어야 합니다");
  const derived = Array.isArray(o.derived) ? o.derived : [];
  derived.forEach((d: unknown, i: number) => {
    const p = `derived[${i}]`;
    if (!isObj(d)) { err(p, "{ name, expr } 또는 { name, lookup, map } 이어야 합니다"); return; }
    const name = str(d.name);
    if (!name || !/^[A-Za-z_][A-Za-z0-9_]{0,40}$/.test(name)) { err(`${p}.name`, "영문 이름이 필요합니다"); return; }
    if (knownVars.has(name)) err(`${p}.name`, `「${name}」 가 두 번 나옵니다`);
    if (d.lookup !== undefined) {
      if (typeof d.lookup !== "string" || !keys.has(d.lookup)) err(`${p}.lookup`, `「${String(d.lookup)}」 가 param_schema 에 없습니다`);
      if (!isObj(d.map) || Object.values(d.map).some((x) => typeof x !== "number")) err(`${p}.map`, "{ 선택값: 숫자 } 객체여야 합니다");
      if (d.default !== undefined && typeof d.default !== "number") err(`${p}.default`, "숫자여야 합니다");
    } else if (d.expr !== undefined) checkExpr(`${p}.expr`, d.expr);
    else err(p, "expr 또는 lookup 이 필요합니다");
    knownVars.add(name);
  });

  // materials
  const materials = isObj(o.materials) ? o.materials : {};
  if (o.materials !== undefined && !isObj(o.materials)) err("materials", "{ 이름: { … } } 객체여야 합니다");
  for (const [name, mat] of Object.entries(materials)) {
    const p = `materials.${name}`;
    if (!isObj(mat)) { err(p, "객체여야 합니다"); continue; }
    if (mat.type !== undefined && mat.type !== "standard") warn(`${p}.type`, "standard 로 그립니다");
    if (mat.color !== undefined && (typeof mat.color !== "string" || !HEX.test(mat.color))) err(`${p}.color`, "#RRGGBB 꼴이어야 합니다");
    for (const k of ["metalness", "roughness", "opacity"]) if (mat[k] !== undefined && (typeof mat[k] !== "number" || (mat[k] as number) < 0 || (mat[k] as number) > 1)) err(`${p}.${k}`, "0 ~ 1 숫자여야 합니다");
    if (mat.emissive !== undefined && (typeof mat.emissive !== "string" || !HEX.test(mat.emissive))) err(`${p}.emissive`, "#RRGGBB 꼴이어야 합니다");
    if (mat.emissiveIntensity !== undefined && (typeof mat.emissiveIntensity !== "number" || mat.emissiveIntensity < 0 || mat.emissiveIntensity > 10)) err(`${p}.emissiveIntensity`, "0 ~ 10 숫자여야 합니다");
    // 무늬 — { type: "stripes", color2?, scale?, angle?(아무 각도), fit?("meters" | "face") } · { type: "carton", color2? } · { type: "label", angle? }
    if (mat.pattern !== undefined) {
      const pt = mat.pattern;
      if (!isObj(pt)) err(`${p}.pattern`, "{ type: \"stripes\" | \"carton\" | \"label\", … } 객체여야 합니다");
      else {
        const known: Record<string, string[]> = { stripes: ["type", "color2", "scale", "angle", "fit"], carton: ["type", "color2"], label: ["type", "angle"] };
        const t = typeof pt.type === "string" && Object.prototype.hasOwnProperty.call(known, pt.type) ? pt.type : null;
        if (!t) err(`${p}.pattern.type`, "stripes · carton · label 중 하나여야 합니다");
        if (pt.color2 !== undefined && (typeof pt.color2 !== "string" || !HEX.test(pt.color2))) err(`${p}.pattern.color2`, "#RRGGBB 꼴이어야 합니다");
        if (t === "stripes" && pt.scale !== undefined && (typeof pt.scale !== "number" || !(pt.scale > 0) || pt.scale > 100)) err(`${p}.pattern.scale`, "0 보다 크고 100 이하인 숫자(1m 당 줄 수 · fit face 면 면 하나에 줄 수)여야 합니다");
        if (t === "stripes" && pt.fit !== undefined && pt.fit !== "meters" && pt.fit !== "face") err(`${p}.pattern.fit`, "\"meters\"(줄 간격 m 기준) 또는 \"face\"(면마다 한 번 늘여 붙임) 여야 합니다");
        if (pt.angle !== undefined && (typeof pt.angle !== "number" || !Number.isFinite(pt.angle))) err(`${p}.pattern.angle`, "숫자(도)여야 합니다");
        if (t) Object.keys(pt).filter((k) => !known[t].includes(k)).forEach((k) => warn(`${p}.pattern.${k}`, "모르는 칸 — 무시합니다"));
      }
    }
  }

  // geometry
  const g = o.geometry as Record<string, unknown>;
  if (g.coordinate_system !== undefined && g.coordinate_system !== "Y_UP") err("geometry.coordinate_system", "Y_UP 만 씁니다");
  if (g.unit !== undefined && g.unit !== "m" && g.unit !== "mm") err("geometry.unit", "m 또는 mm");
  if (g.type !== undefined && g.type !== "parts" && g.type !== "asset") err("geometry.type", "\"parts\" 또는 \"asset\"(GLB)");
  if (g.type === "asset") checkAsset(g, err);
  else if (g.asset !== undefined) err("geometry.asset", "GLB 자산은 geometry.type 이 \"asset\" 일 때만 씁니다");
  if (g.lod_size !== undefined) checkExpr("geometry.lod_size", g.lod_size);
  if (g.type === "asset" && isObj(g.asset) && g.asset.lod === "hi" && !(Array.isArray(g.parts) && g.parts.length)) err("geometry.asset.lod", "GLB 를 가까이서만 쓰려면 멀리서 그릴 parts 가 있어야 합니다");
  if (g.footprint !== undefined) {
    if (!Array.isArray(g.footprint) || g.footprint.length !== 2) err("geometry.footprint", "[가로, 세로] 두 값이어야 합니다");
    else g.footprint.forEach((x, i) => checkExpr(`geometry.footprint[${i}]`, x));
  }
  const parts = Array.isArray(g.parts) ? g.parts : [];
  if (g.type !== "asset" && !parts.length) err("geometry.parts", "도형이 하나도 없습니다");
  if (parts.length > MAX_PARTS) err("geometry.parts", `${MAX_PARTS}개까지입니다`);
  const partIds = new Set<string>();
  const parents = new Map<string, string>();
  parts.forEach((pt: unknown, i: number) => {
    const p = `geometry.parts[${i}]`;
    if (!isObj(pt)) { err(p, "객체여야 합니다"); return; }
    const id = str(pt.id);
    if (!id || !ID.test(id)) err(`${p}.id`, "영문 · 숫자 · _ · - 이름이 필요합니다");
    else if (partIds.has(id)) err(`${p}.id`, `「${id}」 가 두 번 나옵니다`);
    else partIds.add(id);
    if (!PRIMITIVES.includes(pt.type as never)) err(`${p}.type`, pt.type === undefined ? "가 없습니다" : `「${String(pt.type)}」 는 아직 그리지 못합니다 — ${PRIMITIVES.join(" · ")} 중 하나`);
    if (pt.type === "mesh") {
      const v = pt.vertices; const ix = pt.indices;
      if (!Array.isArray(v) || !v.length || v.length % 3 !== 0) err(`${p}.vertices`, "[x,y,z, …] 숫자 3개씩이 필요합니다");
      else if (v.length > MAX_MESH_NUMBERS) err(`${p}.vertices`, `숫자가 너무 많습니다(${MAX_MESH_NUMBERS}개까지)`);
      else if (v.some((n: unknown) => typeof n !== "number" || !Number.isFinite(n))) err(`${p}.vertices`, "숫자만 넣으세요");
      else if (ix !== undefined) {
        const nv = v.length / 3;
        if (Array.isArray(ix) && ix.length > MAX_MESH_INDICES) err(`${p}.indices`, `번호가 너무 많습니다(${MAX_MESH_INDICES}개까지)`);
        else if (!Array.isArray(ix) || ix.length % 3 !== 0) err(`${p}.indices`, `삼각형 꼭짓점 번호(0 ~ ${nv - 1}) 3개씩이어야 합니다`);
        else {
          const bad = ix.findIndex((n: unknown) => !Number.isInteger(n) || (n as number) < 0 || (n as number) >= nv);
          if (bad >= 0) err(`${p}.indices`, `${bad}번째 값 「${String(ix[bad])}」 — 꼭짓점 번호는 0 ~ ${nv - 1} 정수여야 합니다(범위 밖)`);
        }
      } else if (v.length % 9 !== 0) err(`${p}.vertices`, "indices 가 없으면 삼각형마다 꼭짓점 3개(숫자 9개)씩이어야 합니다");
    } else checkVec(`${p}.size`, pt.size, true);
    const live = pt.live === true; // [화물 수량] 매 프레임 — 흐름 값(fitem · fprog · $flow) · 부모 반복 번호 $vars.pi 를 쓸 수 있다
    checkVec(`${p}.position`, pt.position, false, live ? ["pi"] : [], live);
    checkVec(`${p}.rotation`, pt.rotation, false);
    // L4 — 예전엔 경고(회색으로 그림)였다. 2026-10-12 P1 부터 오류(번역 30종 · 라이브러리 예시는 모두 통과 — 테스트가 지킨다)
    if (pt.material !== undefined && (typeof pt.material !== "string" || !hasOwn(materials, pt.material))) err(`${p}.material`, `materials 에 「${String(pt.material)}」 가 없습니다`);
    if (pt.visible !== undefined && typeof pt.visible !== "boolean") { if (live) checkExpr(`${p}.visible`, pt.visible, false, ["pi"], true); else checkFlow(`${p}.visible`, pt.visible); }
    if (pt.preview_only !== undefined && typeof pt.preview_only !== "boolean") err(`${p}.preview_only`, "true · false");
    // [고정밀] 가까이만 · 멀리만 · 부품 키트
    if (pt.live !== undefined && typeof pt.live !== "boolean") err(`${p}.live`, "true · false");
    if (pt.lod !== undefined && pt.lod !== "hi" && pt.lod !== "lo") err(`${p}.lod`, "\"hi\"(가까이 볼 때만) 또는 \"lo\"(멀리 볼 때만)");
    if (pt.lod !== undefined && typeof pt.visible === "string" && /\$flow\./.test(pt.visible)) warn(`${p}.visible`, "lod 를 쓰는 part 의 $flow 보이기 식은 고정밀 전환과 겹칩니다 — 자식 part 에 두세요");
    if (pt.type === "kit") {
      if (typeof pt.kit !== "string" || !KIT_NAMES.includes(pt.kit)) err(`${p}.kit`, `부품 키트 이름 — ${KIT_NAMES.join(" · ")} 중 하나`);
      if (pt.kit_options !== undefined) {
        if (!isObj(pt.kit_options)) err(`${p}.kit_options`, "{ 이름: 값 } 객체여야 합니다");
        else Object.entries(pt.kit_options).forEach(([k, v]) => { if (typeof v !== "number" && typeof v !== "string") err(`${p}.kit_options.${k}`, "숫자 · 식 · 글자만"); else if (typeof v === "string" && /^[-$\d(.]/.test(v.trim())) checkExpr(`${p}.kit_options.${k}`, v); });
      }
    } else {
      if (pt.kit !== undefined) warn(`${p}.kit`, "type 이 \"kit\" 일 때만 씁니다 — 무시합니다");
      if (pt.kit_options !== undefined) warn(`${p}.kit_options`, "type 이 \"kit\" 일 때만 씁니다 — 무시합니다");
    }
    if (pt.parent !== undefined) { if (typeof pt.parent !== "string") err(`${p}.parent`, "part id 여야 합니다"); else if (id) parents.set(id, pt.parent); }
    if (pt.repeat !== undefined) {
      if (!isObj(pt.repeat)) err(`${p}.repeat`, "{ count, offset } 객체여야 합니다");
      else {
        checkExpr(`${p}.repeat.count`, pt.repeat.count); checkVec(`${p}.repeat.offset`, pt.repeat.offset, false);
        checkVec(`${p}.repeat.size_step`, pt.repeat.size_step, false);
        const rv = live ? ["i", "j", "k", "pi"] : ["i", "j", "k"];
        if (pt.repeat.visible_if !== undefined) checkExpr(`${p}.repeat.visible_if`, pt.repeat.visible_if, false, rv, live);
        checkVec(`${p}.repeat.item_position`, pt.repeat.item_position, false, rv, live);
        checkVec(`${p}.repeat.item_rotation`, pt.repeat.item_rotation, false, rv, live);
        checkVec(`${p}.repeat.item_scale`, pt.repeat.item_scale, false, ["i", "j", "k"]);
        for (const n of ["2", "3"]) {
          if (pt.repeat[`count${n}`] !== undefined) checkExpr(`${p}.repeat.count${n}`, pt.repeat[`count${n}`]);
          checkVec(`${p}.repeat.offset${n}`, pt.repeat[`offset${n}`], pt.repeat[`count${n}`] !== undefined);
        }
      }
    }
  });
  parents.forEach((par, id) => {
    if (!partIds.has(par)) { err(`geometry.parts.${id}.parent`, `「${par}」 part 가 없습니다`); return; }
    const seen = new Set([id]);
    let cur: string | undefined = par;
    while (cur) { if (seen.has(cur)) { err(`geometry.parts.${id}.parent`, "부모가 돌고 돕니다(순환)"); break; } seen.add(cur); cur = parents.get(cur); }
  });

  // motion
  const motion = isObj(o.motion) ? o.motion : {};
  if (o.motion !== undefined && !isObj(o.motion)) err("motion", "객체여야 합니다");
  const pathIds = new Set<string>();
  (Array.isArray(motion.paths) ? motion.paths : []).forEach((pa: unknown, i: number) => {
    const p = `motion.paths[${i}]`;
    if (!isObj(pa)) { err(p, "객체여야 합니다"); return; }
    const id = str(pa.id);
    if (!id || !ID.test(id)) err(`${p}.id`, "이름이 필요합니다"); else if (pathIds.has(id)) err(`${p}.id`, `「${id}」 가 두 번 나옵니다`); else pathIds.add(id);
    if (pa.type !== "closed" && pa.type !== "open") err(`${p}.type`, "closed 또는 open");
    const pts = Array.isArray(pa.points) ? pa.points : [];
    if (pts.length < 2) err(`${p}.points`, "점이 2개 이상 필요합니다");
    pts.forEach((pt: unknown, j: number) => checkVec(`${p}.points[${j}]`, pt, true));
  });
  const animIds = new Set<string>();
  const anims = Array.isArray(motion.animations) ? motion.animations : [];
  anims.forEach((an: unknown, i: number) => {
    const p = `motion.animations[${i}]`;
    if (!isObj(an)) { err(p, "객체여야 합니다"); return; }
    const id = str(an.id);
    if (!id || !ID.test(id)) err(`${p}.id`, "이름이 필요합니다"); else if (animIds.has(id)) err(`${p}.id`, `「${id}」 가 두 번 나옵니다`); else animIds.add(id);
    if (typeof an.target !== "string" || !partIds.has(an.target)) err(`${p}.target`, `「${String(an.target)}」 part 가 없습니다`);
    const type = an.type;
    if (!["translate", "rotate", "path", "visibility"].includes(String(type))) err(`${p}.type`, "translate · rotate · path · visibility 중 하나");
    if ((type === "translate" || type === "rotate") && !["x", "y", "z"].includes(String(an.axis))) err(`${p}.axis`, "x · y · z 중 하나");
    if (type === "translate" && an.distance === undefined) err(`${p}.distance`, "가 없습니다(움직이는 거리)");
    if (type === "path" && (typeof an.path !== "string" || !pathIds.has(an.path))) err(`${p}.path`, `「${String(an.path)}」 경로가 motion.paths 에 없습니다`);
    if (an.speed !== undefined) checkFlow(`${p}.speed`, an.speed);
    if (an.distance !== undefined) checkFlow(`${p}.distance`, an.distance);
    if (an.when !== undefined) checkFlow(`${p}.when`, an.when);
    if (an.mode !== undefined && !["loop", "pingpong", "once"].includes(String(an.mode))) err(`${p}.mode`, "loop · pingpong · once 중 하나");
  });
  if (motion.states !== undefined) warn("motion.states", "상태 흐름은 저장만 합니다(아직 움직이지 않음) — 순서 동작은 motion.sequences 로");
  // sequences — 고르기 · 이동 · 붙이기 … 차례로
  const seqs = Array.isArray(motion.sequences) ? motion.sequences : [];
  if (motion.sequences !== undefined && !Array.isArray(motion.sequences)) err("motion.sequences", "배열이어야 합니다");
  const OPS = ["wait", "move", "turn", "moveTo", "scale", "material", "pick", "set", "attach", "detach", "show", "hide", "cycle", "busy", "waitUntil", "during", "start", "parallel", "if", "repeat"];
  const seqIds = new Set(seqs.map((q: unknown) => (isObj(q) ? str(q.id) : null)).filter(Boolean) as string[]);
  const EASES = ["linear", "inout"];
  const MAX_STEPS = 2000; // 순서 하나의 단계 수(안쪽 단계 포함)
  /** 변수 이름 — 이름 또는 이름[식](번호 붙은 변수) */
  const checkVarName = (path: string, v: unknown) => {
    const m = typeof v === "string" ? /^([A-Za-z_][A-Za-z0-9_]*)(?:\[(.+)\])?$/.exec(v) : null;
    if (!m) { err(path, "변수 이름(영문 · 숫자 · _) 또는 이름[식] 이어야 합니다"); return; }
    if (m[2] !== undefined) checkFlow(`${path}[번호]`, m[2]);
  };
  const checkSteps = (steps: unknown, q0: string, depth: number, count: { n: number }) => {
    if (!Array.isArray(steps)) { err(q0, "단계 배열이어야 합니다"); return; }
    if (depth > 6) { err(q0, "parallel · if · repeat 은 6단까지 겹칠 수 있습니다"); return; }
    steps.forEach((st: unknown, j: number) => {
      const q = `${q0}[${j}]`;
      count.n++;
      if (!isObj(st)) { err(q, "객체여야 합니다"); return; }
      const op = String(st.op);
      if (!OPS.includes(op)) { err(`${q}.op`, `${OPS.join(" · ")} 중 하나`); return; }
      const needPart = (k: string) => { if (typeof st[k] !== "string" || !partIds.has(st[k] as string)) err(`${q}.${k}`, `「${String(st[k])}」 part 가 없습니다`); };
      const need = (k: string) => { if (st[k] === undefined) { err(`${q}.${k}`, "가 없습니다"); return false; } return true; };
      if (["move", "turn", "moveTo", "scale", "material", "attach", "detach", "show", "hide"].includes(op)) { needPart("target"); if (st.index !== undefined) checkFlow(`${q}.index`, st.index); if (st.group !== undefined && typeof st.group !== "boolean") err(`${q}.group`, "true · false"); }
      if (op === "move" || op === "turn") {
        if (!["x", "y", "z"].includes(String(st.axis))) err(`${q}.axis`, "x · y · z 중 하나");
        if (need("to")) checkFlow(`${q}.to`, st.to);
        if (need("speed")) checkFlow(`${q}.speed`, st.speed);
        if (op === "move") for (const k of ["avoid", "avoidAngle", "avoidMax"]) if (st[k] !== undefined) checkFlow(`${q}.${k}`, st[k]);
        if (st.ease !== undefined && !EASES.includes(String(st.ease))) err(`${q}.ease`, "linear · inout 중 하나");
        if (op === "turn" && st.shortest !== undefined && typeof st.shortest !== "boolean") err(`${q}.shortest`, "true · false");
      }
      if (op === "moveTo") {
        if (need("to")) checkVec(`${q}.to`, st.to, true, [], true);
        if (st.duration === undefined && st.speed === undefined) err(q, "duration(초) 또는 speed(m/s) 가 필요합니다");
        for (const k of ["duration", "speed", "arc", "avoid", "avoidAngle", "avoidMax"]) if (st[k] !== undefined) checkFlow(`${q}.${k}`, st[k]);
        if (st.arcShape !== undefined && st.arcShape !== "parabola" && st.arcShape !== "sin") err(`${q}.arcShape`, "parabola · sin 중 하나");
        if (st.ease !== undefined && st.ease !== "linear" && st.ease !== "inout") err(`${q}.ease`, "linear · inout 중 하나");
      }
      if (op === "scale") {
        if (need("to")) { if (Array.isArray(st.to)) checkVec(`${q}.to`, st.to, true, [], true); else checkFlow(`${q}.to`, st.to); }
        if (st.duration === undefined && st.speed === undefined) err(q, "duration(초) 또는 speed(배율/초) 가 필요합니다");
        for (const k of ["duration", "speed"]) if (st[k] !== undefined) checkFlow(`${q}.${k}`, st[k]);
        if (st.ease !== undefined && !EASES.includes(String(st.ease))) err(`${q}.ease`, "linear · inout 중 하나");
      }
      if (op === "waitUntil") { if (need("cond")) checkFlow(`${q}.cond`, st.cond); if (st.max !== undefined) checkFlow(`${q}.max`, st.max); }
      if (op === "during") { if (need("seconds")) checkFlow(`${q}.seconds`, st.seconds); if (st.var !== undefined && !str(st.var)) err(`${q}.var`, "변수 이름이어야 합니다"); checkSteps(st.steps, `${q}.steps`, depth + 1, count); }
      if (op === "start") {
        if (typeof st.sequence !== "string" || !seqIds.has(st.sequence)) err(`${q}.sequence`, `「${String(st.sequence)}」 동작 순서가 없습니다`);
        if (st.with !== undefined) { if (!isObj(st.with)) err(`${q}.with`, "{ 변수: 식 } 이어야 합니다"); else Object.entries(st.with).forEach(([k2, v2]) => { if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(k2)) err(`${q}.with`, `「${k2}」 는 변수 이름이 아닙니다`); checkFlow(`${q}.with.${k2}`, v2); }); }
      }
      if (op === "material" && (typeof st.material !== "string" || !hasOwn(materials, st.material))) err(`${q}.material`, `materials 에 「${String(st.material)}」 가 없습니다`);
      if (op === "attach") { needPart("to"); if (st.toIndex !== undefined) checkFlow(`${q}.toIndex`, st.toIndex); }
      if (op === "wait" && need("seconds")) checkFlow(`${q}.seconds`, st.seconds);
      if (op === "pick") { checkVarName(`${q}.var`, st.var); if (need("count")) checkFlow(`${q}.count`, st.count); }
      if (op === "set") { checkVarName(`${q}.var`, st.var); if (need("value")) checkFlow(`${q}.value`, st.value); }
      if (op === "parallel") checkSteps(st.steps, `${q}.steps`, depth + 1, count);
      if (op === "if") { if (need("cond")) checkFlow(`${q}.cond`, st.cond); checkSteps(st.then, `${q}.then`, depth + 1, count); if (st.else !== undefined) checkSteps(st.else, `${q}.else`, depth + 1, count); }
      if (op === "repeat") { if (need("times")) checkFlow(`${q}.times`, st.times); if (st.var !== undefined && !str(st.var)) err(`${q}.var`, "변수 이름이어야 합니다"); checkSteps(st.steps, `${q}.steps`, depth + 1, count); }
    });
  };
  const seqSeen = new Set<string>();
  seqs.forEach((sq: unknown, i: number) => {
    const p = `motion.sequences[${i}]`;
    if (!isObj(sq)) { err(p, "객체여야 합니다"); return; }
    if (!str(sq.id) || !ID.test(String(sq.id))) err(`${p}.id`, "이름이 필요합니다");
    else if (seqSeen.has(String(sq.id).trim())) err(`${p}.id`, `「${String(sq.id).trim()}」 가 두 번 나옵니다`);
    else seqSeen.add(String(sq.id).trim());
    const steps = Array.isArray(sq.steps) ? sq.steps : [];
    if (!steps.length) err(`${p}.steps`, "단계가 없습니다");
    const count = { n: 0 };
    if (sq.for !== undefined) {
      if (!isObj(sq.for) || typeof sq.for.var !== "string" || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(sq.for.var)) err(`${p}.for`, "{ var: 변수 이름, count: 식 } 이어야 합니다");
      else if (sq.for.count === undefined) err(`${p}.for.count`, "가 없습니다"); else checkExpr(`${p}.for.count`, sq.for.count);
    }
    if (sq.auto !== undefined && typeof sq.auto !== "boolean") err(`${p}.auto`, "true · false");
    if (sq.local !== undefined && (!Array.isArray(sq.local) || sq.local.some((n: unknown) => typeof n !== "string" || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(n)))) err(`${p}.local`, "변수 이름 배열이어야 합니다");
    checkSteps(steps, `${p}.steps`, 0, count);
    if (count.n > MAX_STEPS) err(`${p}.steps`, `${MAX_STEPS}단계까지입니다(안쪽 포함 ${count.n})`);
  });

  // metadata.twin.capacity_per_hour — 숫자 또는 파라미터 식
  const twMeta = isObj(o.metadata) && isObj((o.metadata as Record<string, unknown>).twin) ? ((o.metadata as Record<string, unknown>).twin as Record<string, unknown>) : null;
  if (twMeta && twMeta.capacity_per_hour !== undefined) checkExpr("metadata.twin.capacity_per_hour", twMeta.capacity_per_hour);

  // bindings
  const bindings = Array.isArray(o.bindings) ? o.bindings : [];
  if (o.bindings !== undefined && !Array.isArray(o.bindings)) err("bindings", "배열이어야 합니다");
  bindings.forEach((b: unknown, i: number) => {
    const p = `bindings[${i}]`;
    if (!isObj(b)) { err(p, "객체여야 합니다"); return; }
    const src = typeof b.source === "string" ? /^params\.([A-Za-z_][A-Za-z0-9_]*)$/.exec(b.source) : null;
    if (!src) err(`${p}.source`, "params.<이름> 꼴이어야 합니다");
    else if (!keys.has(src[1])) err(`${p}.source`, `「${src[1]}」 가 param_schema 에 없습니다`);
    const t = typeof b.target === "string" ? parseTarget(b.target) : null;
    if (!t) err(`${p}.target`, `「${String(b.target)}」 가 유효하지 않습니다 — geometry.parts.<id>.size|position|rotation.x|y|z · …repeat.count · motion.animations.<id>.speed|distance`);
    else if ((t.kind === "part" || t.kind === "count") && !partIds.has(t.id)) err(`${p}.target`, `「${t.id}」 part 가 없습니다`);
    else if (t.kind === "count" && !parts.some((x: unknown) => isObj(x) && x.id === t.id && x.repeat)) err(`${p}.target`, `「${t.id}」 에 repeat 가 없습니다`);
    else if (t.kind === "anim" && !animIds.has(t.id)) err(`${p}.target`, `「${t.id}」 동작이 없습니다`);
    else if (t.kind === "asset") warn(`${p}.target`, "asset.nodes 연결은 GLB 지원 뒤에 씁니다(지금은 무시)");
    if (b.expression !== undefined) checkExpr(`${p}.expression`, b.expression, true);
    if (b.transform !== undefined && b.transform !== "number" && b.transform !== "int") err(`${p}.transform`, "number 또는 int");
  });

  const twinMeta = isObj(o.metadata) && isObj(o.metadata.twin) ? o.metadata.twin : null;
  if (twinMeta && twinMeta.category !== undefined && !["보관", "이송·분류", "로봇·피킹", "포장", "건물" /* [DG4] 기둥 · 영역 */].includes(String(twinMeta.category))) err("metadata.twin.category", "보관 · 이송·분류 · 로봇·피킹 · 포장 · 건물 중 하나");
  // [N7] 같은 종류(kind) — 문자열이 아니거나 코드판 종류 id 가 아니면 경고(같은 종류 규칙이 안 붙을 뿐 그리기는 됨 — register 도 무시) · CAD 랙(cad) — "rack" | "rack_light" 아니면 오류
  //   (register twinTagOfDefinition 은 모르는 cad 를 조용히 버린다 — 검사기에서 먼저 알려 준다)
  if (twinMeta && twinMeta.kind !== undefined) {
    if (typeof twinMeta.kind !== "string" || !twinMeta.kind.trim()) warn("metadata.twin.kind", "코드판 트윈 종류 id 문자열이어야 합니다(예: \"rack\" · \"pick_station\") — 무시합니다");
    else if (!TWIN_KINDS.includes(twinMeta.kind) && !hasBuiltin(twinMeta.kind)) warn("metadata.twin.kind", `트윈에 「${twinMeta.kind}」 종류가 없습니다 — 같은 종류 규칙(재고 · 보충 · 공통 칸)이 붙지 않습니다`);
  }
  if (twinMeta && twinMeta.flow_own !== undefined && typeof twinMeta.flow_own !== "boolean") err("metadata.twin.flow_own", "true · false 여야 합니다");
  if (twinMeta && twinMeta.cad !== undefined && twinMeta.cad !== "rack" && twinMeta.cad !== "rack_light") err("metadata.twin.cad", "\"rack\"(팔레트랙) 또는 \"rack_light\"(경량랙) — 그 밖의 CAD 렌더러는 없습니다");
  if (twinMeta && twinMeta.cad !== undefined && typeof twinMeta.kind === "string" && (twinMeta.kind === "rack" || twinMeta.kind === "rack_light") && twinMeta.kind !== twinMeta.cad) warn("metadata.twin.cad", `kind 「${twinMeta.kind}」 와 cad 「${String(twinMeta.cad)}」 가 다릅니다 — 개수 키 · 로케이션은 cad 를 따릅니다`);

  if (errors.length) return { ok: false, errors, warnings };

  const definition: StoredDefinition = {
    spec_name: specName!, param_schema: schema,
    ...(derived.length ? { derived: derived as StoredDefinition["derived"] } : {}),
    geometry: o.geometry as StoredDefinition["geometry"],
    bindings: bindings as StoredDefinition["bindings"],
    motion: motion as StoredDefinition["motion"],
    materials: materials as StoredDefinition["materials"],
    metadata: isObj(o.metadata) ? (o.metadata as StoredDefinition["metadata"]) : {},
  };
  // 실제로 풀어 보기 — 식이 0 이하 크기 · 숫자 아님을 만들면 막는다
  const r = resolveDefinition(definition, params);
  derived.forEach((d: unknown, i: number) => { const n = isObj(d) ? str(d.name) : null; if (n && !Number.isFinite(r.vars[n])) err(`derived[${i}]`, `「${n}」 계산 결과가 숫자가 아닙니다(지금 값으로)`); });
  r.parts.forEach((pt, i) => {
    const need = pt.type === "mesh" ? [] : pt.type === "plane" ? [0, 2] : pt.type === "cylinder" ? [0, 1] : [0, 1, 2]; // sphere · hemisphere = 세 축
    const last = itemSize(pt, Math.max(0, pt.counts[0] - 1));
    if (pt.count > 1 && need.some((k) => !(last[k] > 0))) err(`geometry.parts[${i}].repeat.size_step`, `「${pt.id}」 마지막 도형 크기가 0 이하가 됩니다`);
    if (need.some((k) => !(pt.size[k] > 0))) err(`geometry.parts[${i}].size`, `「${pt.id}」 크기가 0 이하이거나 숫자가 아닙니다(지금 값 ${pt.size.map((n) => Math.round(n * 1000) / 1000).join(", ")})`);
    if (!pt.position.every((n) => Number.isFinite(n))) err(`geometry.parts[${i}].position`, `「${pt.id}」 위치 계산 결과가 숫자가 아닙니다(지금 값으로)`);
    if (!pt.rotation.every((n) => Number.isFinite(n))) err(`geometry.parts[${i}].rotation`, `「${pt.id}」 회전 계산 결과가 숫자가 아닙니다(지금 값으로)`);
    if (pt.count >= MAX_REPEAT) warn(`geometry.parts[${i}].repeat.count`, `${MAX_REPEAT}개까지만 그립니다`);
  });
  if (errors.length) return { ok: false, errors, warnings };

  return {
    ok: true, kind: "v2-json", format: format || TWIN3D_V2, specId: JSON_SPEC_ID, specName, model, params: r.params, definition,
    counts: { parts: parts.length, animations: anims.length, sequences: seqs.length, params: schema.length, bindings: bindings.length, materials: Object.keys(materials).length, derived: derived.length },
    warnings, notes: [],
  };
}

/**
 * DB 에 저장된 정의(StoredDefinition — format · spec_id 칸이 없다) 검사 — 서버 저장(PUT model · import) 과 트윈 팔레트 읽기가 같이 쓴다.
 * format 을 요구하지 않는다(저장본에는 없다). 파라미터 값(twin_params)도 그 정의의 param_schema 로 함께 본다.
 */
export function validateStoredDefinition(def: unknown, params?: unknown): Validation {
  if (!isObj(def)) return { ok: false, errors: [{ path: "(정의)", message: "{ … } 객체여야 합니다" }], warnings: [] };
  if (params !== undefined && params !== null && !isObj(params)) return { ok: false, errors: [{ path: "params", message: "{ 이름: 값 } 객체여야 합니다" }], warnings: [] };
  const { format: _f, spec_id: _s, model: _m, params: _p, ...rest } = def; void _f; void _s; void _m; void _p;
  return validateTwinJson({ ...rest, format: TWIN3D_V2, spec_id: JSON_SPEC_ID, params: isObj(params) ? params : {} }, () => false);
}
